import { uuidv7 } from '../platform/uuid';
import { toRelayDateTime } from './relay';
import type { Store } from './port';
import type { CandidateExamMetadataRow, ClientRow } from './rows';

/**
 * Die eine `Client`-Zeile: dieses Gerät.
 *
 * Sie trägt zugleich die laufende Auswahl (`currentExamID`, `currentCandidateID`,
 * `currentExaminerID`, `currentActorID`) — Relay führt sie dort, und damit steht der
 * Wiedereinstieg in der Datenbank statt in einem eigenen Feld.
 */

export interface DeviceInfo {
  deviceIdentifier: string;
  deviceName: string;
  clientType: ClientRow['clientType'];
  appVersion: string | null;
  osVersion: string | null;
}

/**
 * Findet oder legt die Gerätezeile an — anhand `deviceIdentifier`, nicht anhand einer separat
 * gemerkten `id`: die kennt das Gerät beim allerersten Start noch nicht, die Geräte-ID ist der
 * einzige feste Anker.
 *
 * ⚠️ **Trägt keine Zeile diese ID, aber es gibt schon eine, übernimmt sie die neue ID** (seit
 * 2026-10-10: Stage-ID statt Hersteller-ID). Ihre `id` bleibt — an ihr hängen die Einstellungen.
 *
 * ⚠️ Bei einer vorhandenen Zeile werden **nur** die Felder aus `info` überschrieben
 * (`{ ...vorhanden, ...info }`). Die laufende Auswahl (`currentExamID` &c.) steht nicht in
 * `DeviceInfo` und bleibt deshalb über einen Neustart hinweg erhalten — genau das macht den
 * Wiedereinstieg in `hydrate()` erst möglich.
 */
export async function ensureClient(store: Store, info: DeviceInfo): Promise<ClientRow> {
  const [gleich] = await store.all<ClientRow>('Client', { deviceIdentifier: info.deviceIdentifier });
  // shortcut: nimmt bei mehreren alten Zeilen irgendeine; nur `ensureClient` legt Zeilen an, also gibt es eine.
  const [vorhanden] = gleich ? [gleich] : await store.all<ClientRow>('Client');
  const zeile: ClientRow = vorhanden
    ? { ...vorhanden, ...info }
    : {
        id: uuidv7(),
        ...info,
        currentExamID: null,
        currentCandidateID: null,
        currentExaminerID: null,
        currentActorID: null,
        currentStatus: 'INACTIVE',
        online: 'N',
        batteryLevelPercent: null,
        voltageStatus: null,
        freeSpaceKb: null,
      };
  await store.put('Client', [zeile]);
  return zeile;
}

export async function updateClientSelection(
  store: Store,
  auswahl: Pick<ClientRow, 'id' | 'currentExamID' | 'currentCandidateID' | 'currentExaminerID' | 'currentActorID'>,
): Promise<void> {
  const [vorhanden] = await store.all<ClientRow>('Client', { id: auswahl.id });
  if (!vorhanden) return;
  await store.put('Client', [{ ...vorhanden, ...auswahl }]);
}

/**
 * Der Zustand eines Prüflings in dieser Prüfung. Er ersetzt die vier eigenen Schlüssel der
 * Oberfläche durch die Relay-Werteliste — der offene Punkt aus `memory/HIER-WEITERMACHEN.md`
 * ist damit entschieden.
 */
export async function setCandidateStatus(
  store: Store,
  examId: string,
  candidateId: string,
  status: CandidateExamMetadataRow['status'],
): Promise<void> {
  const [vorhanden] = (
    await store.all<CandidateExamMetadataRow>('CandidateExamMetadata', { examID: examId })
  ).filter((z) => z.candidateID === candidateId);
  await store.put('CandidateExamMetadata', [
    {
      id: vorhanden?.id ?? uuidv7(),
      candidateID: candidateId,
      examID: examId,
      status,
      remainingSeconds: vorhanden?.remainingSeconds ?? null,
      completedItems: vorhanden?.completedItems ?? null,
      totalItems: vorhanden?.totalItems ?? null,
      lastStartDate: status === 'RUNNING' ? toRelayDateTime(new Date()) : (vorhanden?.lastStartDate ?? null),
      lastModifiedDate: toRelayDateTime(new Date()),
    },
  ]);
}

/** Zustand je Prüfling dieser Prüfung, Schlüssel ist die Candidate-Kennung. */
export async function candidateStatus(
  store: Store,
  examId: string,
): Promise<Record<string, CandidateExamMetadataRow['status']>> {
  const zeilen = await store.all<CandidateExamMetadataRow>('CandidateExamMetadata', { examID: examId });
  return Object.fromEntries(zeilen.map((z) => [z.candidateID, z.status]));
}
